import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { post_purchase_bank_details } from "../../api/ControllApi";
import { ASSET_BASE } from "../../utils/baseUrls";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { MenuItem, FormControl, InputLabel, Select, Tooltip } from "@mui/material";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DownloadIcon from "@mui/icons-material/Download";

const TransactionDetailsModal = ({ open, onClose, propertyId, transactionId, type, onSuccess, isPreview, data }) => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [otherDetails, setOtherDetails] = useState("");
  const [registrationDates, setRegistrationDates] = useState(["", "", ""]);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const IMAGE_BASE_URL = `${ASSET_BASE}storage/bank_images/`;

  const handleSubmit = async () => {
    // Validation
    if (!amount || amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (!paymentMode) {
      toast.error("Please select a payment mode");
      return;
    }
    if (!registrationDates[0]) {
      toast.error("Please select the primary registration date");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time for comparison

    const oneWeekFromNow = new Date(today);
    oneWeekFromNow.setDate(today.getDate() + 7);

    // Validate each provided date
    for (const dateStr of registrationDates) {
      if (dateStr) {
        const selectedDate = new Date(dateStr);
        if (selectedDate < today) {
          toast.error(`Past dates are not allowed: ${dateStr}`);
          return;
        }
        if (selectedDate < oneWeekFromNow) {
          toast.error(`Please select a date at least one week from today: ${dateStr}`);
          return;
        }
      }
    }

    if (!image) {
      toast.error("Please upload a receipt (Image or PDF)");
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("property_id", propertyId);
    formData.append("amount", amount);
    formData.append("payment_mode", paymentMode);
    formData.append("other_details", otherDetails);
    // Append each non-empty date as registration_date[]
    registrationDates.forEach((date) => {
      if (date) {
        formData.append("registration_date[]", date);
      }
    });
    formData.append("type", type);
    if (transactionId) {
      formData.append("transaction_id", transactionId); // Using the booking transaction ID
    }
    formData.append("image", image);

    try {
      const res = await post_purchase_bank_details(formData);
      if (res.success || res.status == 200 || res.status == 201 || res.status == true) {
        toast.success(res.message || "Details submitted successfully");
        onSuccess && onSuccess();
        onClose();
        // Reset form
        setAmount("");
        setPaymentMode("");
        setOtherDetails("");
        setRegistrationDates(["", "", ""]);
        setImage(null);

        // Redirect if coming from rental enquiry
        // if (type === "rental") {
        //   navigate("/dashboard/buying-list?tab=syndicate");
        // }
      } else {
        // Handle specific validation errors from backend
        if (res.errors) {
          Object.values(res.errors).forEach((errorGroup) => {
            if (Array.isArray(errorGroup)) {
              errorGroup.forEach((err) => toast.error(err));
            } else {
              toast.error(errorGroup);
            }
          });
        } else {
          toast.error(res.message || "Submission failed");
        }
      }
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.errors) {
        Object.values(errorData.errors).forEach((errorGroup) => {
          if (Array.isArray(errorGroup)) {
            errorGroup.forEach((e) => toast.error(e));
          } else {
            toast.error(errorGroup);
          }
        });
      } else {
        toast.error(errorData?.message || err.message || "Error submitting details");
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculate min date (7 days from today) for input restriction
  const getMinDate = () => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().split("T")[0];
  };

  const handleClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isPreview ? "Transaction Details" : "Add Transaction Details"}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isPreview && data ? (
          (() => {
            const previewItem = Array.isArray(data) ? data[0] : data;
            if (!previewItem) return <Typography>No data available</Typography>;

            const imageUrl = previewItem.image_url || (previewItem.image ? `${IMAGE_BASE_URL}${previewItem.image}` : null);

            let displayDates = [];
            try {
              if (previewItem.registration_date) {
                if (typeof previewItem.registration_date === 'string') {
                  const parsed = JSON.parse(previewItem.registration_date);
                  displayDates = Array.isArray(parsed) ? parsed : [parsed];
                } else if (Array.isArray(previewItem.registration_date)) {
                  displayDates = previewItem.registration_date;
                } else {
                  displayDates = [previewItem.registration_date];
                }
              }
            } catch (e) {
              displayDates = [previewItem.registration_date];
            }

            const handleDownload = () => {
              if (imageUrl) {
                const link = document.createElement("a");
                link.href = imageUrl;
                link.target = "_blank";
                link.download = imageUrl.split("/").pop(); // Attempt to give it a file name
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            };

            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Amount</Typography>
                  <Typography sx={{ fontWeight: 600 }}>₹{previewItem.amount || "-"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Payment Mode</Typography>
                  <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                    {previewItem.payment_mode ? previewItem.payment_mode.replace("_", " ") : "-"}
                  </Typography>
                </Box>
                {/* <Box>
                  <Typography variant="caption" color="text.secondary">Transaction ID</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{previewItem.transaction_id || "-"}</Typography>
                </Box> */}
                <Box>
                  <Typography variant="caption" color="text.secondary">Registration Dates</Typography>
                  {displayDates.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                      {displayDates.map((date, idx) => (
                        <Box key={idx} sx={{ backgroundColor: 'primary.light', color: 'white', px: 1.5, py: 0.5, borderRadius: '50px', fontSize: '0.8rem' }}>
                          {date}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography sx={{ fontWeight: 600 }}>-</Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Other Details</Typography>
                  <Typography sx={{ fontWeight: 600 }}>{previewItem.other_details || "-"}</Typography>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">Receipt / Image Preview</Typography>
                    {imageUrl && (
                      <Tooltip title="Download File">
                        <IconButton size="small" color="primary" onClick={handleDownload} sx={{ border: '1px solid', borderColor: 'divider' }}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {imageUrl ? (
                    imageUrl.toLowerCase().endsWith(".pdf") ? (
                      <Box sx={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #ddd', height: "500px" }}>
                        <iframe
                          src={`${imageUrl}#toolbar=1`}
                          title="PDF Preview"
                          style={{ width: "100%", height: "100%", border: "none" }}
                        />
                      </Box>
                    ) : (
                      <Box sx={{ border: "1px solid #ddd", borderRadius: '12px', overflow: 'hidden', position: 'relative', backgroundColor: '#f9f9f9', height: "500px", display: 'flex', flexDirection: 'column' }}>
                        <TransformWrapper
                          initialScale={1}
                          minScale={0.5}
                          maxScale={4}
                        >
                          {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                            <>
                              <Box sx={{ 
                                position: 'absolute', 
                                top: 10, 
                                right: 10, 
                                zIndex: 10, 
                                display: 'flex', 
                                gap: 1, 
                                backgroundColor: 'rgba(255, 255, 255, 0.8)', 
                                padding: '4px', 
                                borderRadius: '8px', 
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)' 
                              }}>
                                <Tooltip title="Zoom In">
                                  <IconButton size="small" onClick={() => zoomIn()}>
                                    <ZoomInIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Zoom Out">
                                  <IconButton size="small" onClick={() => zoomOut()}>
                                    <ZoomOutIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reset">
                                  <IconButton size="small" onClick={() => resetTransform()}>
                                    <RestartAltIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <TransformComponent
                                wrapperStyle={{
                                  width: "100%",
                                  height: "100%",
                                }}
                                contentStyle={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center"
                                }}
                              >
                                <img
                                  src={imageUrl}
                                  alt="Receipt"
                                  style={{
                                    maxWidth: "100%",
                                    maxHeight: "100%",
                                    objectFit: "contain",
                                    cursor: 'grab'
                                  }}
                                  onMouseDown={(e) => e.target.style.cursor = 'grabbing'}
                                  onMouseUp={(e) => e.target.style.cursor = 'grab'}
                                />
                              </TransformComponent>
                            </>
                          )}
                        </TransformWrapper>
                      </Box>
                    )
                  ) : (
                    <Typography color="text.secondary">No file uploaded</Typography>
                  )}
                </Box>
              </Box>
            );
          })()
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Amount"
              type="number"
              fullWidth
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
            <FormControl fullWidth required>
              <InputLabel id="payment-mode-label">Payment Mode</InputLabel>
              <Select
                labelId="payment-mode-label"
                id="payment-mode-select"
                value={paymentMode}
                label="Payment Mode"
                onChange={(e) => setPaymentMode(e.target.value)}
              >
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="dd">DD</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="others">Others</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Registration Date 1"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getMinDate() }}
              value={registrationDates[0]}
              onChange={(e) => {
                const newDates = [...registrationDates];
                newDates[0] = e.target.value;
                setRegistrationDates(newDates);
              }}
              required
            />
            <TextField
              label="Registration Date 2"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getMinDate() }}
              value={registrationDates[1]}
              onChange={(e) => {
                const newDates = [...registrationDates];
                newDates[1] = e.target.value;
                setRegistrationDates(newDates);
              }}
            />
            <TextField
              label="Registration Date 3"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: getMinDate() }}
              value={registrationDates[2]}
              onChange={(e) => {
                const newDates = [...registrationDates];
                newDates[2] = e.target.value;
                setRegistrationDates(newDates);
              }}
            />
            <TextField
              label="Bank Details"
              multiline
              rows={3}
              fullWidth
              value={otherDetails}
              onChange={(e) => setOtherDetails(e.target.value)}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Upload Receipt (Image or PDF) *
              </Typography>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ py: 2, borderStyle: "dashed" }}
              >
                {image ? image.name : "Choose File"}
                <input
                  type="file"
                  hidden
                  accept="image/*,.pdf"
                  onChange={(e) => setImage(e.target.files[0])}
                />
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading} color={isPreview ? "primary" : "inherit"}>
          {isPreview ? "Close" : "Cancel"}
        </Button>
        {!isPreview && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "primary.main",
              px: 4,
              borderRadius: '50px',
              textTransform: 'none'
            }}
          >
            {loading ? "Submitting..." : "Submit Details"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransactionDetailsModal;
